// src/components/EnviarCorreoModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import './EnviarCorreoModal.css';
import { db, storage, functions } from '../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { ref, uploadBytes } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../context/AuthContext';
import { FaCloudUploadAlt, FaFileImage, FaFilePdf, FaFileVideo, FaTrash, FaPaperclip } from 'react-icons/fa';
import { calcularEstadoMoroso, useConfiguracionFinanciera } from '../utils/morosoUtils';
import { pushToSyncQueue } from '../utils/offlineSyncQueue';

const EmailChip = ({ email, onRemove }) => (
  <span className="email-chip">
    {email}
    <button type="button" aria-label={`Quitar ${email}`} onClick={() => onRemove(email)}>×</button>
  </span>
);

const EnviarCorreoModal = ({ isOpen, onClose }) => {
  const { fraccionamientoId } = useAuth();
  const financialConfig = useConfiguracionFinanciera(fraccionamientoId);
  const [loading, setLoading] = useState(false);
  const [userCatalog, setUserCatalog] = useState([]);
  const [selected, setSelected] = useState([]);
  const [filter, setFilter] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectMorosos, setSelectMorosos] = useState(false);
  const [selectTodos, setSelectTodos] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [chipsPage, setChipsPage] = useState(1);
  const itemsPerPage = 3;
  const chipsPerPage = 8;

  const normalizeText = (value) => String(value || '').trim().toLowerCase();
  const toNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };
  const pickEmail = (user) => {
    return (
      user?.email ||
      user?.correo ||
      user?.correoElectronico ||
      null
    );
  };
  const isCasetaPerfil = (perfil) => {
    const p = normalizeText(perfil).replace(/\s+/g, '');
    return p.includes('caseta') || p.includes('guardia') || p.includes('seguridad');
  };

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        if (!fraccionamientoId) { setUserCatalog([]); return; }

        // 1) Cargar casas y construir set de correos morosos por adeudo > 0

        // 2) Cargar casas y construir set de correos morosos por adeudo > 0
        const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
        const casasSnap = await getDocs(casasRef);
        const morosoEmailSet = new Set();
        casasSnap.forEach((docSnap) => {
          const casa = docSnap.data() || {};
          const adeudoCasa = toNumber(casa?.adeudo ?? 0);

          const cuotaMensual = Number(financialConfig.cuotaMensual || 0);
          const mesesPermitidos = Number(financialConfig.mesesPermitidos || 1);

          if (calcularEstadoMoroso(adeudoCasa, cuotaMensual, mesesPermitidos)) {
            if (isCasetaPerfil(casa?.perfil)) return;
            const emailCasa = (
              casa?.correo ||
              casa?.email ||
              casa?.correoElectronico ||
              casa?.emailResidente ||
              casa?.correoResidente ||
              ''
            ).trim();
            if (emailCasa) morosoEmailSet.add(emailCasa.toLowerCase());
          }
        });

        // 3) Cargar usuarios y marcar morosos según set construido
        const usuariosRef = collection(db, 'usuarios');
        const snap = await getDocs(query(usuariosRef, where('fraccionamientoId', '==', fraccionamientoId)));
        if (cancelled) return;

        const items = [];

        // Agregar usuarios registrados
        snap.forEach((docSnap) => {
          const data = docSnap.data() || {};
          if (isCasetaPerfil(data?.perfil || data?.rol)) {
            return;
          }
          const email = pickEmail(data);
          if (!email) {
            return;
          }
          const emailLower = email.trim().toLowerCase();

          const casaDoc = Array.from(casasSnap.docs).find(d => {
            const c = d.data();
            return normalizeText(c.email) === emailLower ||
              normalizeText(c.correo) === emailLower ||
              normalizeText(c.correoElectronico) === emailLower;
          });

          const casaData = casaDoc ? casaDoc.data() : null;
          const casaId = casaDoc ? casaDoc.id : null;

          items.push({
            email: email.trim(),
            isMoroso: morosoEmailSet.has(emailLower),
            numero: casaData?.numero || casaData?.numeroCasa || '',
            nombre: casaData?.nombre_residente || casaData?.nombre || '',
            casaId,
            origin: 'user'
          });
        });

        // 4) Agregar registros de 'casas' que tengan email y no estén ya en items.
        // Antes sólo agregábamos si el perfil incluía 'propietario' o 'arrendador',
        // eso hacía que algunas casas compartiendo email no aparecieran en la lista.
        casasSnap.forEach((docSnap) => {
          const casa = docSnap.data() || {};
          const perfil = normalizeText(casa.perfil || casa.rolAsignado || '');

          if (isCasetaPerfil(perfil)) return; // nunca incluir perfiles de caseta/guardia

          const email = (
            casa.email || casa.correo || casa.correoElectronico || ''
          ).trim();

          if (email) {
            const emailLower = email.toLowerCase();
            const casaId = docSnap.id;
            // Evitar duplicar la misma casa (mismo casaId) si ya fue agregada
            const alreadyHasCasa = items.some(it => it.casaId && it.casaId === casaId);

            if (!alreadyHasCasa) {
              const adeudo = toNumber(casa.adeudo ?? 0);
              const cuotaMensual = Number(financialConfig.cuotaMensual || 0);
              const mesesPermitidos = Number(financialConfig.mesesPermitidos || 1);

              // Si no existe ninguna entrada para este casaId, añadimos la casa como item
              items.push({
                email: email,
                isMoroso: calcularEstadoMoroso(adeudo, cuotaMensual, mesesPermitidos),
                numero: casa.numero || casa.numeroCasa || '',
                nombre: casa.nombre_residente || casa.nombre || '',
                perfilRef: casa.perfil || casa.rolAsignado || '',
                casaId,
                origin: 'casa'
              });
            }
          }
        });

        // Agrupar por email (insensible a mayúsculas) y mantener todas las casas/residentes
        // dentro del mismo email. Marcar la primera entrada de cada grupo como primaria
        // (`selectablePrimary`) y las demás como duplicadas (`isDuplicate`). Esto permite
        // mostrar todas las casas detectadas pero solo permitir interacción en la primaria.
        const groups = items.reduce((acc, item) => {
          const key = String(item.email || '').trim().toLowerCase();
          if (!acc[key]) acc[key] = [];
          acc[key].push(item);
          return acc;
        }, {});

        const expanded = Object.values(groups).flatMap((group) => {
          group.sort((a, b) => {
            const na = a.numero ? String(a.numero) : '';
            const nb = b.numero ? String(b.numero) : '';
            if (na && nb) return na.localeCompare(nb);
            if (na) return -1;
            if (nb) return 1;
            return (a.nombre || '').localeCompare(b.nombre || '');
          });
          return group.map((it, idx) => ({ ...it, isDuplicate: idx > 0, selectablePrimary: idx === 0 }));
        });

        const dedup = expanded.sort((a, b) => {
          const ea = a.email.toLowerCase();
          const eb = b.email.toLowerCase();
          if (ea === eb) {
            if (a.selectablePrimary && !b.selectablePrimary) return -1;
            if (!a.selectablePrimary && b.selectablePrimary) return 1;
            return 0;
          }
          return ea.localeCompare(eb);
        });

        setUserCatalog(dedup);
      } catch (e) {
        console.error('Cargar emails error', e);
        setUserCatalog([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, fraccionamientoId, financialConfig]);

  // (historial se mueve fuera del modal)

  const filteredCatalog = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return userCatalog;

    // Si parece un correo (contiene @), buscar por email de forma estricta (startsWith)
    if (f.includes('@')) {
      return userCatalog.filter((u) => String(u.email || '').toLowerCase().startsWith(f));
    }

    // Si es un número (número de casa), hacer match por inicio
    const digitsOnly = /^[0-9]+$/;
    if (digitsOnly.test(f)) {
      return userCatalog.filter((u) => String(u.numero || '').toLowerCase().startsWith(f));
    }

    // Para búsqueda por nombre, requerir al menos 3 caracteres para evitar resultados demasiado amplios
    if (f.length < 3) return [];

    return userCatalog.filter((u) => String(u.nombre || '').toLowerCase().includes(f) || String(u.numero || '').toLowerCase().includes(f));
  }, [userCatalog, filter]);

  const morosoEmails = useMemo(() => userCatalog.filter((u) => u.isMoroso).map((u) => u.email), [userCatalog]);
  const allEmails = useMemo(() => userCatalog.map((u) => u.email), [userCatalog]);

  const toggleSelect = (email) => {
    setSelected(prev => prev.includes(email) ? prev.filter(x => x !== email) : [...prev, email]);
  };

  const handleSelectMorosos = (checked) => {
    setSelectMorosos(checked);
    if (checked) {
      setSelected((prev) => Array.from(new Set([...prev, ...morosoEmails])));
    } else {
      setSelected((prev) => prev.filter((e) => !morosoEmails.includes(e)));
    }
  };

  const handleSelectTodos = (checked) => {
    setSelectTodos(checked);
    if (checked) {
      setSelected((prev) => Array.from(new Set([...prev, ...allEmails])));
      setSelectMorosos(true);
    } else {
      setSelected([]);
      setSelectMorosos(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  const totalPages = Math.ceil(filteredCatalog.length / itemsPerPage);
  const paginatedCatalog = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredCatalog.slice(start, start + itemsPerPage);
  }, [filteredCatalog, currentPage]);

  const totalChipsPages = Math.ceil(selected.length / chipsPerPage);
  const paginatedSelected = useMemo(() => {
    const start = (chipsPage - 1) * chipsPerPage;
    return selected.slice(start, start + chipsPerPage);
  }, [selected, chipsPage]);

  useEffect(() => {
    if (chipsPage > totalChipsPages && totalChipsPages > 0) {
      setChipsPage(totalChipsPages);
    }
  }, [selected.length, totalChipsPages, chipsPage]);

  const removeChip = (email) => setSelected(prev => prev.filter(x => x !== email));

  const handleFiles = (evt) => {
    const f = Array.from(evt.target.files || []);
    setFiles(f);
  };

  const addTypedRecipient = () => {
    const val = filter.trim();
    if (!val) return;
    const valLower = val.toLowerCase();

    // Intentar buscar por email exacto/insensible
    let found = userCatalog.find((u) => String(u.email || '').trim().toLowerCase() === valLower);

    // Si no es correo, permitir búsqueda por nombre o número (si tiene al menos 3 caracteres)
    if (!found) {
      if (valLower.includes('@')) {
        setError('El correo no pertenece al condominio.');
        return;
      }
      if (valLower.length >= 3) {
        found = userCatalog.find((u) => String(u.nombre || '').toLowerCase().includes(valLower) || String(u.numero || '').toLowerCase() === valLower);
      }
    }

    if (!found) {
      setError('No se encontró un destinatario coincidente.');
      return;
    }

    setError('');
    setSelected(prev => prev.includes(found.email) ? prev : [...prev, found.email]);
    setFilter('');
  };

  const handleSend = async () => {
    setError('');
    setSuccess('');
    if (selected.length === 0) {
      setError('Selecciona al menos un destinatario.');
      return;
    }
    if (!subject.trim()) {
      setError('Captura un asunto para el correo.');
      return;
    }
    setLoading(true);
    try {
      if (!navigator.onLine) {
        const base64Files = [];
        for (const file of files) {
          try {
            const base64 = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.readAsDataURL(file);
              reader.onload = () => resolve(reader.result);
              reader.onerror = error => reject(error);
            });
            base64Files.push({
              name: file.name,
              type: file.type,
              base64
            });
          } catch(err) {
            console.error('Error convirtiendo archivo a base64:', err);
          }
        }

        pushToSyncQueue('sendCustomBulletin', {
          fraccionamientoId: fraccionamientoId || null,
          to: selected,
          subject: subject.trim(),
          messageHtml: message && message.trim() ? message.trim() : null,
          base64Files
        });

        setSuccess('Guardado para envío automático cuando se restablezca la conexión.');
        setSubject('');
        setMessage('');
        setFiles([]);
        setSelected([]);
        setTimeout(() => onClose(), 2000);
        return;
      }

      // upload attachments (optional) and pass storage paths for server-side attachment
      const storagePaths = [];
      for (const file of files) {
        const path = `boletines/${fraccionamientoId || 'general'}/${Date.now()}-${file.name}`;
        const r = ref(storage, path);
        const res = await uploadBytes(r, file, { contentType: file.type || undefined });
        storagePaths.push({ name: file.name, path: res.metadata.fullPath, contentType: file.type || undefined });
      }

      const callable = httpsCallable(functions, 'sendCustomBulletin');
      const res = await callable({
        fraccionamientoId: fraccionamientoId || null,
        to: selected,
        subject: subject.trim(),
        messageHtml: message && message.trim() ? message.trim() : null,
        storagePaths,
      });
      const data = res && res.data ? res.data : {};
      if (data && data.success) {
        setSuccess('Correo enviado correctamente.');
        // reset minimal
        setSubject('');
        setMessage('');
        setFiles([]);
        setSelected([]);
        // Cerrar modal después de 2 segundos
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setError(data && data.message ? data.message : 'No se pudo enviar.');
      }
    } catch (e) {
      console.error('send bulletin error', e);
      setError(e && e.message ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="mail-modal-overlay" role="dialog" aria-modal="true">
      <div className="mail-modal">
        <div className="mail-modal-header">
          <h3>Nuevo mensaje</h3>
          <button className="mail-close" onClick={onClose} aria-label="Cerrar">×</button>
        </div>
        <div className="mail-modal-body">
          <div className="mail-field">
            <div className="mail-field-header">
              <label>Redactar nuevo Correo para...</label>
              {selected.length > 0 && (
                <span className="mail-selection-counter">
                  {selected.length} {selected.length === 1 ? 'destinatario seleccionado' : 'destinatarios seleccionados'}
                </span>
              )}
            </div>
            <div className="mail-bulk-selectors">
              <label>
                <input
                  type="checkbox"
                  checked={selectMorosos}
                  onChange={(e) => handleSelectMorosos(e.target.checked)}
                />
                <span>Seleccionar solo morosos!!</span>
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={selectTodos}
                  onChange={(e) => handleSelectTodos(e.target.checked)}
                />
                <span>Seleccionar todos!!</span>
              </label>
            </div>
            <div className="mail-recipients">
              <div className="mail-recipients-chips">
                {paginatedSelected.map(e => (
                  <EmailChip key={e} email={e} onRemove={removeChip} />
                ))}
              </div>

              {totalChipsPages > 1 && (
                <div className="mail-chips-pagination">
                  <button
                    type="button"
                    className="chips-pagination-btn"
                    disabled={chipsPage === 1}
                    onClick={() => setChipsPage(prev => Math.max(1, prev - 1))}
                  >
                    ‹
                  </button>
                  <span className="chips-pagination-info">
                    {chipsPage} / {totalChipsPages}
                  </span>
                  <button
                    type="button"
                    className="chips-pagination-btn"
                    disabled={chipsPage === totalChipsPages}
                    onClick={() => setChipsPage(prev => Math.min(totalChipsPages, prev + 1))}
                  >
                    ›
                  </button>
                </div>
              )}

              <input
                type="text"
                value={filter}
                onChange={e => setFilter(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTypedRecipient(); } }}
                placeholder="Agregar destinatarios..."
              />
            </div>
            <div className="mail-catalog">
              {loading ? (
                <div className="mail-hint">Cargando correos…</div>
              ) : (
                <>
                  <div className="mail-catalog-list">
                    {paginatedCatalog.map(user => (
                      <label key={`${user.email}-${user.numero || ''}`} className="mail-catalog-item">
                        <input
                          type="checkbox"
                          checked={selected.includes(user.email)}
                          onChange={() => toggleSelect(user.email)}
                          disabled={!user.selectablePrimary}
                          title={user.isDuplicate ? 'Duplicado: selecciona la entrada primaria para elegir este email' : ''}
                        />
                        <div className="mail-catalog-info">
                          <div className="mail-catalog-main">
                            <span className="mail-catalog-email">{user.email}</span>
                            {user.isMoroso && <span className="mail-badge">Moroso</span>}
                          </div>
                          {(user.numero || user.nombre || user.isDuplicate) && (
                            <div className="mail-catalog-sub">
                              {user.numero && <span className="mail-catalog-num">Casa {user.numero}</span>}
                              {user.nombre && <span className="mail-catalog-name">{user.nombre}</span>}
                              {user.isDuplicate && <span className="mail-duplicate-note">(mismo email en otra casa)</span>}
                            </div>
                          )}
                        </div>
                      </label>
                    ))}
                    {filteredCatalog.length === 0 && !loading && (
                      <div className="mail-hint">No se encontraron resultados.</div>
                    )}
                  </div>

                  {totalPages > 1 && (
                    <div className="mail-pagination">
                      <button
                        type="button"
                        className="pagination-btn"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      >
                        Anterior
                      </button>
                      <span className="pagination-info">
                        Página <strong>{currentPage}</strong> de {totalPages}
                      </span>
                      <button
                        type="button"
                        className="pagination-btn"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      >
                        Siguiente
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="mail-field">
            <label>Asunto</label>
            <input type="text" value={subject} onChange={e => setSubject(e.target.value)} placeholder="Asunto del correo" />
          </div>

          <div className="mail-field">
            <label>Mensaje</label>
            <textarea rows={8} value={message} onChange={e => setMessage(e.target.value)} placeholder="Escribe tu mensaje aquí…" />
          </div>

          <div className="mail-field">
            <label>Adjuntar archivos</label>
            <div className="mail-upload-container">
              <input
                type="file"
                id="mail-file-input"
                multiple
                onChange={handleFiles}
                className="mail-file-hidden"
              />
              <label htmlFor="mail-file-input" className="mail-upload-dropzone">
                <FaCloudUploadAlt className="upload-icon" />
                <div className="upload-text">
                  <span className="upload-main-text">Haz clic para subir archivos</span>
                  <span className="upload-sub-text">Soporta Documentos, Imágenes y Videos</span>
                </div>
              </label>

              <div className="mail-upload-specs">
                <div className="spec-item">
                  <FaFileImage className="spec-icon img" />
                  <span>Imágenes (5MB)</span>
                </div>
                <div className="spec-item">
                  <FaFilePdf className="spec-icon doc" />
                  <span>Documentos (10MB)</span>
                </div>
                <div className="spec-item">
                  <FaFileVideo className="spec-icon vid" />
                  <span>Videos (20MB)</span>
                </div>
              </div>
            </div>

            {files.length > 0 && (
              <div className="mail-files-preview">
                {files.map((f, idx) => (
                  <div key={`${f.name}-${idx}`} className="file-preview-card">
                    <FaPaperclip className="file-icon" />
                    <span className="file-name">{f.name}</span>
                    <button
                      type="button"
                      onClick={() => setFiles(prev => prev.filter((_, i) => i !== idx))}
                      className="remove-file-btn"
                    >
                      <FaTrash />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && <div className="mail-error">{error}</div>}
          {success && <div className="mail-success">{success}</div>}
        </div>
        <div className="mail-modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={loading}>Cancelar</button>
          <button className="btn-primary" onClick={handleSend} disabled={loading}>{loading ? 'Enviando…' : 'Enviar'}</button>
        </div>
      </div>
    </div>
  );
};

export default EnviarCorreoModal;
